import { Box, Tab, Tabs, Typography } from '@mui/material';
import ScheduleBox from './components/ScheduleBox';
import useNswagClient from '../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import { CountZonesResponse, StockCheckScheduleDetail } from '../../app/services/api/generated';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import CountingUOMBox from './components/CountingUomsBox';
import { ConfigurationName } from '../../enums/ConfigurationName';
import useLogError from '../../hooks/useLogError';
import CountZonesBox from './components/CountZonesBox';
import { useNavigate, useParams } from 'react-router-dom';

const tabIndexToValue: Record<string, string> = {
  '1': 'one',
  '2': 'two',
  '3': 'three',
};
const tabValueToIndex: Record<string, string> = {
  'one': '1',
  'two': '2',
  'three': '3',
};

const StockCountSchedule = () => {
  const { getsStockCheckSchedule, getConfiguration, getCountZones } = useNswagClient();
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);
  const storeID = selectedStore?.storeNumber ? selectedStore.storeNumber : 'No store selected';
  const isStoreSelected = () => { if (selectedStore == null) { return false; } else { return true; } };
  const [schedules, setSchedules] = useState<StockCheckScheduleDetail[]>([]);
  const [tabValue, setTabValue] = useState<string>('one');
  const [isCountingInDifferentUom, setIsCountingInDifferentUom] = useState<boolean>(false);
  const [isCountZonesEnabled, setIsCountZonesEnabled] = useState<boolean>(false);
  const [zones, setZones] = useState<CountZonesResponse[]>([]);
  const [isSchedulesLoading, setIsSchedulesLoading] = useState(false);
  const [isZonesLoading, setIsZonesLoading] = useState(false);
  const [zonesLoaded, setZonesLoaded] = useState(false);
  const { logError } = useLogError();
  const navigate = useNavigate();
  const params = useParams<{ tab?: string }>();

  useEffect(() => {
    loadData();
    getCountingInDifferentUOMConfig();
    getCountZonesEnabledConfig();
  }, [selectedStore]);

  useEffect(() => {
    let nextTab = 'one';
    if (params.tab && tabIndexToValue[params.tab]) {
      const candidate = tabIndexToValue[params.tab];
      if (
        candidate === 'one' ||
                (candidate === 'two' && isCountingInDifferentUom) ||
                (candidate === 'three' && isCountZonesEnabled)
      ) {
        nextTab = candidate;
      }
    }
    setTabValue(nextTab);
  }, [params.tab, isCountingInDifferentUom, isCountZonesEnabled]);

  useEffect(() => {
    if (tabValue === 'three' && isCountZonesEnabled && !zonesLoaded) {
      loadZones();
    }
  }, [tabValue, isCountZonesEnabled]);

  const loadData = () => {
    setIsSchedulesLoading(true);
    getsStockCheckSchedule(storeID)
      .then((result) => {
        if (result?.data) setSchedules(result.data);
      })
      .catch(logError)
      .finally(() => setIsSchedulesLoading(false));
  };

  const loadZones = () => {
    setIsZonesLoading(true);
    getCountZones(storeID)
      .then((result) => {
        if (result?.data) setZones(result.data);
        setZonesLoaded(true);
      })
      .catch(logError)
      .finally(() => setIsZonesLoading(false));
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    navigate(`/stock-count-schedule/${tabValueToIndex[newValue]}`);

    if (newValue === 'three') {
      loadZones();
    }
  };


  const getCountingInDifferentUOMConfig = () => {
    getConfiguration(ConfigurationName.CountingInDifferentUoms, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((config: any) => {
        if (config.data.value === 'true') {
          setIsCountingInDifferentUom(true);
        }
      })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any) => logError(e));
  };

  const getCountZonesEnabledConfig = () => {
    getConfiguration(ConfigurationName.CountZonesWithItemsEnabled, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((config: any) => {
        if (config.data.value === 'true') {
          setIsCountZonesEnabled(true);
        }
      })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any) => logError(e));
  };
  
  return (
    <Box sx={{ width: '100%', textAlign: 'left' }}>
      <PageArea>
        <PageTitle>{t('stockCountManagement')}</PageTitle>
        {isStoreSelected() ? <>
          <Tabs value={tabValue}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
            aria-label="primary tabs example">
            <Tab label={t('scheduledStockCounts')}
              value="one" />
            {isCountingInDifferentUom && <Tab label={t('countingUOMs')}
              value="two" />}
            {isCountZonesEnabled && <Tab label={t('countZones')}
              value="three" />}
          </Tabs>
          {tabValue === 'one' && <ScheduleBox schedules={schedules}
            storeID={storeID}
            loadData={loadData}
            isLoading={isSchedulesLoading}></ScheduleBox>}
          {tabValue === 'two' && isCountingInDifferentUom && <CountingUOMBox></CountingUOMBox>}
          {tabValue === 'three' && isCountZonesEnabled && <CountZonesBox zones={zones}
            storeID={storeID}
            loadData={loadZones}
            isLoading={isZonesLoading}></CountZonesBox>}
        </>
          : <Box><Typography variant='textLG'>No store selected</Typography></Box>}
      </PageArea>
    </Box>
  );
};

export default StockCountSchedule;